import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { ObjectAttributeField } from "src/api/generated/erp/common/objectAttributes/model/objectAttributeField.ts";
import { ObjectAttributeFieldComponent } from "src/components/views/erp/objectAttributes/ObjectAttributeFieldComponent.tsx";
import {
	AddShopOrdersToBatchDataGridApi,
	AddShopOrdersToBatchDataGridApi_AttributeFilter,
} from "src/api/generated/erp/production/shopOrderBatch/api/addShopOrdersToBatchDataGridApi.ts";

export interface AddShopOrdersToBatchAttributeFiltersProps {
	siteId: number;
	productionLineId: number | null | undefined;
	filterValues: AddShopOrdersToBatchDataGridApi_AttributeFilter[];
	onSubmit: (values: AddShopOrdersToBatchDataGridApi_AttributeFilter[]) => void;
	onlyAlreadyConnectedParts?: boolean;
	shopOrderBatchId?: number;
}

export const AddShopOrdersToBatchAttributeFilters = ({
	siteId,
	productionLineId,
	filterValues,
	onSubmit,
	onlyAlreadyConnectedParts = false,
	shopOrderBatchId,
}: AddShopOrdersToBatchAttributeFiltersProps) => {
	const tenantCustomizations = useTenantCustomizations();
	const fieldIds = tenantCustomizations.erp?.production?.addShopOrdersToBatchView?.attributeFilterFields ?? [];

	if (fieldIds.length === 0) return null;

	return (
		<AsyncFetchRender
			noContainer
			fetch={() =>
				AddShopOrdersToBatchDataGridApi.getAttributeFilterFields({
					siteId: siteId,
					productionLineId: productionLineId,
					attributeFieldIds: fieldIds,
					onlyAlreadyConnectedParts: onlyAlreadyConnectedParts,
					shopOrderBatchId: shopOrderBatchId,
				})
			}
			content={(fields: ObjectAttributeField[]) => (
				<>
					{fields.map((field) => (
						<RenderField
							key={field.id}
							field={field}
							currentFilters={filterValues}
							onSubmit={(value) => onSubmitField(field, value)}
						/>
					))}
				</>
			)}
		/>
	);

	function onSubmitField(field: ObjectAttributeField, encodedValue: string | null) {
		const withoutOld = filterValues.filter((f) => f.attributeFieldId !== field.id);
		const next =
			encodedValue == null || encodedValue === "" || encodedValue === "[]" ?
				withoutOld
			:	withoutOld.concat({
					attributeFieldId: field.id,
					attributeValue: encodedValue,
				});
		onSubmit(next);
	}
};

interface RenderFieldProps {
	field: ObjectAttributeField;
	currentFilters: AddShopOrdersToBatchDataGridApi_AttributeFilter[];
	onSubmit: (value: string | null) => void;
}

const RenderField = ({ field, currentFilters, onSubmit }: RenderFieldProps) => {
	// Find current filter value for this field
	const initialValue = currentFilters.find((f) => f.attributeFieldId === field.id)?.attributeValue ?? null;

	return (
		<ObjectAttributeFieldComponent
			sx={{
				flex: "0 1 auto",
			}}
			fieldDefinition={field}
			value={initialValue}
			onChange={(v) => onSubmit(v)}
		/>
	);
};
